// src/components/CategoryCarousel.js
import React, { useRef, useEffect, useState, useContext } from "react";
import { LanguageContext } from "../context/LanguageContext";
import { useNavigate } from "react-router-dom";
import { getPublicCategories } from "../services/categories.api";
import { getAllProducts } from "../services/products.api";
import { mapBackendProduct } from "../utils/mapProduct";

const TEXT = {
  EN: { heading: "SHOP BY CATEGORY" },
  AR: { heading: "تسوقي حسب الفئة" },
};

function CategoryCarousel() {
  const { lang } = useContext(LanguageContext);
  const navigate = useNavigate();
  const scrollRef = useRef(null);

  const [categories, setCategories] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const cardWidth = 190;

  useEffect(() => {
    async function load() {
      try {
        const rawCats = await getPublicCategories();

const cats = Array.from(
  new Map(rawCats.map((c) => [c.slug, c])).values()
);

        const enriched = await Promise.all(
          cats.map(async (c) => {
            const products = await getAllProducts({
              category: c.slug,
              limit: 1,
            });

            const mapped = products.slice(0, 1).map(mapBackendProduct);

            return {
              key: c.slug,
              title: lang === "AR" ? c.name_ar : c.name_en,
              image:
                mapped[0]?.images?.[0] ||
                "/images/placeholder-product.jpg",
            };
          })
        );

        setCategories(enriched);
      } catch (e) {
        console.error(e);
      }
    }

    load();
  }, [lang]);

  const logicalLength = categories.length;
  const looped = [...categories, ...categories, ...categories];

  const snapToIndex = (i, smooth = true) => {
    scrollRef.current?.scrollTo({
      left: i * cardWidth,
      behavior: smooth ? "smooth" : "auto",
    });
  };

  useEffect(() => {
    if (logicalLength) {
      setCurrentIndex(logicalLength);
      snapToIndex(logicalLength, false);
    }
  }, [logicalLength]);

  const scrollLeft = () => {
    const newIndex = currentIndex - 1;
    setCurrentIndex(newIndex);
    snapToIndex(newIndex);

    if (newIndex === logicalLength - 1) {
      setTimeout(() => {
        const reset = logicalLength * 2 - 1;
        setCurrentIndex(reset);
        snapToIndex(reset, false);
      }, 350);
    }
  };

  const scrollRight = () => {
    const newIndex = currentIndex + 1;
    setCurrentIndex(newIndex);
    snapToIndex(newIndex);

    if (newIndex === logicalLength * 2) {
      setTimeout(() => {
        setCurrentIndex(logicalLength);
        snapToIndex(logicalLength, false);
      }, 350);
    }
  };

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const idx = Math.round(scrollRef.current.scrollLeft / cardWidth);
    setCurrentIndex(idx);
  };

  const realActiveIndex = currentIndex % logicalLength;

  const handleClick = (key) => {
  if (["new-in", "sale", "bestsellers"].includes(key)) {
    navigate(`/products/${key}`);
  } else {
    navigate(`/products/category/${key}`);
  }
};


  return (
    <div className="w-full mt-6 mb-6 relative">
      {/* HEADER */}
      <div
        className="flex items-center justify-between px-4 mb-4"
        dir={lang === "AR" ? "rtl" : "ltr"}
      >
        <button onClick={scrollLeft} className="z-20">
          <span className="text-lg font-bold text-[#2E5A2A]">
            {lang === "AR" ? "›" : "<"}
          </span>
        </button>

        <h2 className="text-lg md:text-2xl font-bold tracking-wide text-[#2E5A2A]">
          {TEXT[lang].heading}
        </h2>

        <button onClick={scrollRight} className="z-20">
          <span className="text-lg font-bold text-[#2E5A2A]">
            {lang === "AR" ? "‹" : ">"}
          </span>
        </button>
      </div>

      {/* SCROLLER */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        dir="ltr"
        className="
          flex gap-4 px-4
          overflow-x-auto overflow-y-hidden
          scrollbar-hide
          snap-x snap-mandatory
        "
      >
        {looped.map((cat, i) => {
          const logicalIndex = i % logicalLength;
          const isActive = logicalIndex === realActiveIndex;

          return (
            <button
              key={i}
              onClick={() => handleClick(cat.key)}
              className={`
  relative snap-center
  min-w-[150px] md:min-w-[260px] lg:min-w-[320px]
  bg-[#0E0F0E]/70 backdrop-blur-sm
  border border-[#2E5A2A]/30
  overflow-hidden
  transition-all duration-300
  group card-3d
  ${
    isActive
      ? "neon-pulse ring-2 ring-[#2E5A2A]"
      : "hover:scale-105"
  }
`}
            >
              {/* IMAGE */}
              <div className="relative">
                <img
                  src={cat.image}
                  alt={cat.title}
                  className="
                    w-full h-40 md:h-80 lg:h-[340px]
                    object-cover
                    transition-transform duration-700
                    group-hover:scale-110
                    sniper-lens
                  "
                />

                {/* HUD GRID */}
                <div className="absolute inset-0 hud-grid opacity-0 group-hover:opacity-40 pointer-events-none"></div>

                {/* BULLET TRAIL */}
                <div className="absolute inset-0 bullet-trail opacity-0 group-hover:opacity-100 pointer-events-none"></div>

                {/* RADAR */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="radar-sweep"></div>
                </div>

                {/* CROSSHAIR */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-0 group-hover:opacity-100 transition-all">
                  <div className="crosshair-category"></div>
                </div>
              </div>

              {/* TITLE */}
              <div className="
  absolute bottom-0 left-0 w-full
  bg-gradient-to-t from-black/80 via-black/40 to-transparent
  px-3 py-3
">
  <p className="
    text-xs md:text-sm lg:text-base
    font-semibold tracking-wide
    text-[#9CB8A1]
    text-center
  ">
    {cat.title}
  </p>
</div>
            </button>
          );
        })}
      </div>

      {/* INDICATORS */}
      <div className="w-full flex mt-4 px-6 gap-1 justify-center">
        {categories.map((_, i) => (
          <div
            key={i}
            className="h-[3px] rounded-full transition-all duration-300"
            style={{
              width: realActiveIndex === i ? "28px" : "8px",
              backgroundColor:
                realActiveIndex === i ? "#2E5A2A" : "#C8D7C8",
            }}
          />
        ))}
      </div>
    </div>
  );
}

export default CategoryCarousel;
